/**
 * The mapping sheet — every target field on one screen.
 *
 * A migration maps dozens of fields, and doing that in a 300px inspector column
 * is why people give up and map in Excel instead. This is the same mapping,
 * full-screen, laid out the way the work actually goes: **one row per target
 * field**, required ones first, with what is still missing stated at the top.
 *
 * Two things it does that a pair of dropdowns cannot:
 *
 * - **A constant.** `source_system__c = "Legacy PV"` on every row. Without it
 *   you add a transform upstream whose only job is to repeat a literal.
 * - **A value map.** Source systems say "Active" where Vault wants `active__v`.
 *   Doing that with a chain of Replace transforms puts the translation table
 *   somewhere other than the mapping it belongs to — and the picklist's real
 *   values are right here to map onto.
 */

import { useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconArrowRight, IconPlus, IconTrash } from "@tabler/icons-react";

import type { FieldDefinition } from "@/shared/api/integration-browse";

export interface Mapping {
  column?: string;
  field: string;
  constant?: string;
  valueMap?: Record<string, string>;
}

/** A field worth offering. Vault reports dozens it fills itself; a mapping
 *  sheet listing those is a sheet nobody can find anything in. */
function mappable(field: FieldDefinition): boolean {
  return field.editable !== false && field.name !== "id";
}

function ValueMapEditor({
  field,
  mapping,
  columnValues,
  onChange,
}: {
  field: FieldDefinition | undefined;
  mapping: Mapping;
  columnValues: string[];
  onChange: (next: Mapping) => void;
}) {
  const pairs = Object.entries(mapping.valueMap ?? {});
  const allowed = (field?.picklist_values ?? []).map((v) => v.name);

  const setPair = (index: number, from: string, to: string) => {
    const next = Object.fromEntries(pairs.map(([f, t], i) => (i === index ? [from, to] : [f, t])));
    onChange({ ...mapping, valueMap: next });
  };

  return (
    <Stack gap={4}>
      {pairs.map(([from, to], i) => (
        <Group key={i} gap={4} wrap="nowrap">
          <Select
            size="xs"
            placeholder="source value"
            data={Array.from(new Set([...columnValues, from].filter(Boolean)))}
            value={from || null}
            onChange={(v) => setPair(i, v ?? "", to)}
            searchable
            allowDeselect={false}
            style={{ flex: 1 }}
          />
          <IconArrowRight size={12} />
          <Select
            size="xs"
            placeholder="Veeva value"
            data={Array.from(new Set([...allowed, to].filter(Boolean)))}
            value={to || null}
            onChange={(v) => setPair(i, from, v ?? "")}
            searchable
            allowDeselect={false}
            style={{ flex: 1 }}
          />
          <ActionIcon
            size="sm"
            variant="subtle"
            color="red"
            aria-label="Remove value mapping"
            onClick={() =>
              onChange({
                ...mapping,
                valueMap: Object.fromEntries(pairs.filter((_, j) => j !== i)),
              })
            }
          >
            <IconTrash size={12} />
          </ActionIcon>
        </Group>
      ))}
      <Button
        size="compact-xs"
        variant="subtle"
        leftSection={<IconPlus size={12} />}
        onClick={() => onChange({ ...mapping, valueMap: { ...(mapping.valueMap ?? {}), "": "" } })}
      >
        Map a value
      </Button>
      {pairs.length > 0 && (
        <Text size="xs" c="dimmed">
          Anything not listed goes through unchanged — so Vault names it rather than silently
          receiving a blank.
        </Text>
      )}
    </Stack>
  );
}

export function MappingSheet({
  opened,
  onClose,
  fields,
  columns,
  mappings,
  onChange,
  sampleRows = [],
  objectLabel,
}: {
  opened: boolean;
  onClose: () => void;
  fields: FieldDefinition[];
  columns: string[];
  mappings: Mapping[];
  onChange: (next: Mapping[]) => void;
  /** A few rows from the source, to offer real values in a value map. */
  sampleRows?: Record<string, unknown>[];
  objectLabel: string;
}) {
  const [filter, setFilter] = useState("");
  const [show, setShow] = useState<"all" | "required" | "mapped" | "unmapped">("all");

  const byField = useMemo(() => new Map(mappings.map((m) => [m.field, m])), [mappings]);
  const offered = useMemo(() => fields.filter(mappable), [fields]);

  const missingRequired = offered.filter((f) => f.required && !byField.has(f.name));
  const unusedColumns = columns.filter((c) => !mappings.some((m) => m.column === c));

  const visible = offered.filter((field) => {
    const mapped = byField.has(field.name);
    if (show === "required" && !field.required) return false;
    if (show === "mapped" && !mapped) return false;
    if (show === "unmapped" && mapped) return false;
    const needle = filter.trim().toLowerCase();
    return (
      !needle ||
      field.name.toLowerCase().includes(needle) ||
      (field.label ?? "").toLowerCase().includes(needle)
    );
  });

  const setMapping = (field: string, next: Mapping | null) => {
    const others = mappings.filter((m) => m.field !== field);
    onChange(next ? [...others, next] : others);
  };

  const valuesIn = (column: string | undefined): string[] => {
    if (!column) return [];
    const seen = new Set<string>();
    for (const row of sampleRows) {
      const value = row[column];
      if (value !== null && value !== undefined && String(value).trim()) {
        seen.add(String(value).trim());
      }
    }
    return Array.from(seen).slice(0, 50);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Map columns to ${objectLabel}`}
      fullScreen
      styles={{ body: { display: "flex", flexDirection: "column", height: "calc(100vh - 60px)" } }}
    >
      <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
        <Group justify="space-between" wrap="wrap">
          <Group gap="xs">
            <TextInput
              placeholder="Find a field"
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
              w={220}
            />
            <SegmentedControl
              size="xs"
              value={show}
              onChange={(v) => setShow(v as typeof show)}
              data={[
                { value: "all", label: `All (${offered.length})` },
                { value: "required", label: "Required" },
                { value: "mapped", label: `Mapped (${mappings.length})` },
                { value: "unmapped", label: "Unmapped" },
              ]}
            />
          </Group>
          <Group gap="xs">
            {missingRequired.length > 0 ? (
              <Tooltip label={missingRequired.map((f) => f.name).join(", ")}>
                <Badge color="orange" variant="light">
                  {missingRequired.length} required field
                  {missingRequired.length === 1 ? "" : "s"} unmapped
                </Badge>
              </Tooltip>
            ) : (
              <Badge color="teal" variant="light">
                every required field is mapped
              </Badge>
            )}
            {unusedColumns.length > 0 && (
              <Tooltip label={unusedColumns.join(", ")}>
                <Badge color="gray" variant="light">
                  {unusedColumns.length} column{unusedColumns.length === 1 ? "" : "s"} unused
                </Badge>
              </Tooltip>
            )}
          </Group>
        </Group>

        <Alert color="gray" variant="light" p="xs">
          <Text size="xs">
            Vault marks some fields required that it fills itself, so &ldquo;required&rdquo; here is
            a prompt, not a rule — the load is the judge. Mapping nothing at all sends the rows
            through with their own column names.
          </Text>
        </Alert>

        <ScrollArea style={{ flex: 1, minHeight: 0 }}>
          <Table striped highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: "28%" }}>Veeva field</Table.Th>
                <Table.Th style={{ width: "22%" }}>Comes from</Table.Th>
                <Table.Th>Value</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {visible.map((field) => {
                const mapping = byField.get(field.name);
                const mode = mapping ? (mapping.column ? "column" : "constant") : "none";
                return (
                  <Table.Tr key={field.name}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" ff="monospace">
                          {field.name}
                        </Text>
                        {field.required && (
                          <Badge size="xs" color="orange" variant="light">
                            required
                          </Badge>
                        )}
                        {field.unique && (
                          <Badge size="xs" color="blue" variant="light">
                            unique
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {field.label}
                        {field.max_length ? ` · max ${field.max_length}` : ""}
                        {field.type ? ` · ${field.type}` : ""}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <SegmentedControl
                        size="xs"
                        fullWidth
                        value={mode}
                        onChange={(v) => {
                          if (v === "none") setMapping(field.name, null);
                          else if (v === "column")
                            setMapping(field.name, { field: field.name, column: columns[0] ?? "" });
                          else setMapping(field.name, { field: field.name, constant: "" });
                        }}
                        data={[
                          { value: "none", label: "—" },
                          { value: "column", label: "Column" },
                          { value: "constant", label: "Fixed" },
                        ]}
                      />
                    </Table.Td>
                    <Table.Td>
                      {mode === "column" && mapping && (
                        <Stack gap={4}>
                          <Select
                            size="xs"
                            placeholder={columns.length ? "Pick a column" : "Test the source first"}
                            data={Array.from(
                              new Set([...columns, mapping.column ?? ""].filter(Boolean)),
                            )}
                            value={mapping.column || null}
                            onChange={(v) =>
                              setMapping(field.name, { ...mapping, column: v ?? "" })
                            }
                            searchable
                          />
                          <ValueMapEditor
                            field={field}
                            mapping={mapping}
                            columnValues={valuesIn(mapping.column)}
                            onChange={(next) => setMapping(field.name, next)}
                          />
                        </Stack>
                      )}
                      {mode === "constant" && mapping && (
                        <TextInput
                          size="xs"
                          placeholder="the same value on every row"
                          value={mapping.constant ?? ""}
                          onChange={(e) =>
                            setMapping(field.name, {
                              ...mapping,
                              constant: e.currentTarget.value,
                            })
                          }
                        />
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          {visible.length === 0 && (
            <Text size="sm" c="dimmed" p="md">
              No fields match.
            </Text>
          )}
        </ScrollArea>

        <Group justify="flex-end">
          <Button onClick={onClose}>Done</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
